import type { MenuItem } from "../../Types/Layout/Sidebar"
import { jwtDecode } from "jwt-decode"

interface DecodedToken {
  exp: number
  user_id: number
  email: string
  is_superuser: boolean
  [key: string]: any
}

// Add this constant at the top of the file, after the interface definitions
const AUTH_CHECK_INTERVAL = 5000

// Professional Menu State Management
class MenuStateManager {
  private static instance: MenuStateManager
  private menuList: MenuItem[] = []
  private isLoading = false
  private error: string | null = null
  private lastFetchTime = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  private readonly STORAGE_KEY = "cached_menu_data"
  private readonly STORAGE_TIMESTAMP_KEY = "cached_menu_timestamp"
  private subscribers: Set<() => void> = new Set()
  private authCheckInterval: NodeJS.Timeout | null = null
  private currentUserType: "superuser" | "regular" | "unauthenticated" = "unauthenticated"
  public AUTH_CHECK_INTERVAL = 5000

  private constructor() {
    this.initializeAuthMonitoring()
    this.loadCachedMenus()
    this.initializeMenus()
  }

  public static getInstance(): MenuStateManager {
    if (!MenuStateManager.instance) {
      MenuStateManager.instance = new MenuStateManager()
    }
    return MenuStateManager.instance
  }

  // Subscribe to menu state changes
  public subscribe(callback: () => void): () => void {
    this.subscribers.add(callback)
    return () => this.subscribers.delete(callback)
  }

  // Notify all subscribers of state changes
  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => {
      try {
        callback()
      } catch (error) {
        console.error("Error in menu state subscriber:", error)
      }
    })

    // Also notify the menu update callbacks
    notifyMenuUpdate()
  }

  // Initialize authentication monitoring
  private initializeAuthMonitoring(): void {
    // Monitor localStorage changes for cross-tab synchronization
    window.addEventListener("storage", (e) => {
      if (e.key === "accessToken") {
        this.handleAuthChange()
      }
    })

    // Monitor focus events to refresh on tab switch
    window.addEventListener("focus", () => {
      this.handleAuthChange()
    })

    // Periodic auth check (every 30 seconds)
    this.authCheckInterval = setInterval(() => {
      this.handleAuthChange()
    }, 30000)
  }

  // Handle authentication state changes
  private async handleAuthChange(): Promise<void> {
    const newUserType = this.getCurrentUserType()

    if (newUserType !== this.currentUserType) {
      this.currentUserType = newUserType
      await this.initializeMenus(true)
    }
  }

  // Get current user type
  private getCurrentUserType(): "superuser" | "regular" | "unauthenticated" {
    if (!this.isAuthenticated()) return "unauthenticated"
    return this.isUserSuperuser() ? "superuser" : "regular"
  }

  // Load cached menus from localStorage
  private loadCachedMenus(): void {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY)
      const cachedTimestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY)

      if (cachedData && cachedTimestamp) {
        const timestamp = Number.parseInt(cachedTimestamp, 10)
        const now = Date.now()

        // Use cached data if it's still valid
        if (now - timestamp < this.CACHE_DURATION) {
          this.menuList = JSON.parse(cachedData)
          this.lastFetchTime = timestamp
          this.notifySubscribers()
        }
      }
    } catch (error) {
      console.warn("Failed to load cached menus:", error)
      this.clearCache()
    }
  }

  // Cache menus to localStorage with validation
  private cacheMenus(menus: MenuItem[]): void {
    try {
      if (!Array.isArray(menus)) {
        console.warn("Cannot cache non-array menu data")
        return
      }

      const timestamp = Date.now()
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(menus))
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, timestamp.toString())
      this.lastFetchTime = timestamp

      console.log("Menus cached successfully:", menus.length, "sections")
    } catch (error) {
      console.warn("Failed to cache menus:", error)
    }
  }

  // Clear menu cache
  private clearCache(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY)
      this.lastFetchTime = 0
    } catch (error) {
      console.warn("Failed to clear menu cache:", error)
    }
  }

  // Get authentication token
  private getAuthToken(): string | null {
    return localStorage.getItem("accessToken")
  }

  // Decode JWT token
  private getDecodedToken(): DecodedToken | null {
    const token = this.getAuthToken()
    if (!token) return null

    try {
      const decoded: DecodedToken = jwtDecode(token)
      const isExpired = decoded.exp * 1000 < Date.now()

      if (isExpired) {
        this.clearAuthData()
        return null
      }

      return decoded
    } catch {
      return null
    }
  }

  // Clear authentication data
  private clearAuthData(): void {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("user")
    this.clearCache()
  }

  // Check if user is superuser
  private isUserSuperuser(): boolean {
    const decoded = this.getDecodedToken()
    return decoded?.is_superuser || false
  }

  // Check if user is authenticated
  private isAuthenticated(): boolean {
    return this.getDecodedToken() !== null
  }

  // Helper method to validate and normalize menu structure
  private validateMenuStructure(menuData: any[]): any[] {
    if (!Array.isArray(menuData)) {
      console.warn("Menu data is not an array, returning empty array")
      return []
    }

    return menuData.filter((section) => {
      if (!section || typeof section !== "object") {
        console.warn("Invalid section found in menu data:", section)
        return false
      }

      if (!section.title) {
        console.warn("Section missing title:", section)
        return false
      }

      if (!Array.isArray(section.Items)) {
        console.warn("Section Items is not an array:", section)
        section.Items = []
      }

      return true
    })
  }

  // Transform API response to match expected frontend structure
  private transformApiResponse(menuData: any[]): MenuItem[] {
    console.log("🔄 Transforming API response:", menuData)

    const processMenuItem = (item: any, depth = 0): any => {
      const indent = "  ".repeat(depth)

      console.log(`${indent}📋 Processing item:`, {
        title: item.title,
        originalType: item.type || item.menu_type,
        path: item.path,
        hasChildren: !!(item.children && item.children.length > 0),
      })

      // Get the original type from backend (could be 'type' or 'menu_type')
      const originalType = item.type || item.menu_type || "link"

      // Process children recursively if they exist
      const processedChildren =
        item.children && item.children.length > 0
          ? item.children.map((child: any) => processMenuItem(child, depth + 1))
          : undefined

      // Determine final type based on backend data:
      // 1. If backend says 'sub' -> keep as 'sub'
      // 2. If backend says 'link' but has children -> change to 'sub'
      // 3. If backend says 'link' and no children -> keep as 'link'
      let finalType = originalType
      if (originalType === "link" && processedChildren && processedChildren.length > 0) {
        finalType = "sub"
        console.log(`${indent}🔄 Converting '${item.title}' from 'link' to 'sub' because it has children`)
      }

      const processedItem = {
        ...item,
        // Transform path
        path: item.path ? `${process.env.PUBLIC_URL}${item.path}` : undefined,
        // Set the final type based on backend data and children
        type: finalType,
        // Ensure required properties exist
        active: item.active !== undefined ? item.active : false,
        icon: item.icon || "circle",
        lanClass: item.lanClass || "",
        id: item.id || Math.random(),
        // Set processed children
        children: processedChildren,
      }

      console.log(`${indent}✅ Processed item:`, {
        title: processedItem.title,
        originalType: originalType,
        finalType: processedItem.type,
        hasChildren: !!processedItem.children,
        childrenCount: processedItem.children?.length || 0,
      })

      return processedItem
    }

    const processedData = menuData.map((section: any) => {
      console.log("🏷️ Processing section:", section.title)

      const processedSection = {
        ...section,
        lanClass: section.lanClass || "",
        Items: section.Items ? section.Items.map((item: any) => processMenuItem(item)) : [],
      }

      console.log("✅ Processed section:", {
        title: processedSection.title,
        itemCount: processedSection.Items.length,
        items: processedSection.Items.map((item: any) => ({
          title: item.title,
          originalType: item.type || item.menu_type,
          finalType: item.type,
          hasChildren: !!item.children,
          childrenCount: item.children?.length || 0,
        })),
      })

      return processedSection
    })

    console.log("🎯 Final transformed data:", processedData)
    return processedData
  }

  // Enhanced method to add PUBLIC_URL prefix and transform structure
  private addPublicUrlPrefix(menuData: any[]): MenuItem[] {
    console.log("🔍 Starting menu transformation:", menuData)

    // First validate the structure
    const validatedData = this.validateMenuStructure(menuData)

    // Then transform the API response to match expected structure
    const transformedData = this.transformApiResponse(validatedData)

    return transformedData
  }

  // Load dynamic menus from backend with retry logic and validation
  private async loadDynamicMenus(): Promise<MenuItem[]> {
    try {
      const token = this.getAuthToken()
      if (!token || !this.isAuthenticated()) return []

      const baseURL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"

      console.log("🌐 Fetching dynamic menus from:", `${baseURL}/api/core/user/menus/`)

      // Add timeout and retry logic
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

      const response = await fetch(`${baseURL}/api/core/user/menus/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, clear auth data
          localStorage.removeItem("accessToken")
          localStorage.removeItem("refreshToken")
          localStorage.removeItem("user")
          // Trigger re-initialization
          setTimeout(() => this.initializeMenus(true), 100)
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const menuData = await response.json()

      // Add debugging to see the raw menu data
      console.log("📥 Raw menu data from backend:", menuData)

      // Process the menu data with transformation
      const processedMenuData = this.addPublicUrlPrefix(menuData)

      // Add debugging to see the processed menu data
      console.log("🎯 Final processed menu data:", processedMenuData)

      return processedMenuData
    } catch (error) {
      console.error("❌ Error loading dynamic menus:", error)
      return []
    }
  }

  // Initialize menus based on user type with improved caching
  public async initializeMenus(force = false) {
    // Prevent multiple simultaneous calls
    if (this.isLoading && !force) return

    const now = Date.now()
    const newUserType = !this.isAuthenticated() ? "unauthenticated" : this.isUserSuperuser() ? "superuser" : "regular"

    console.log("🚀 Initializing menus:", {
      force,
      newUserType,
      currentUserType: this.currentUserType,
      isAuthenticated: this.isAuthenticated(),
      isSuperuser: this.isUserSuperuser(),
    })

    // Check if we need to update
    if (
      !force &&
      this.currentUserType === newUserType &&
      this.menuList.length > 0 &&
      now - this.lastFetchTime < this.CACHE_DURATION
    ) {
      console.log("⏭️ Skipping menu initialization - using cached data")
      return
    }

    this.isLoading = true
    this.currentUserType = newUserType
    this.lastFetchTime = now

    try {
      if (!this.isAuthenticated()) {
        // Clear menu for unauthenticated users
        console.log("🔒 User not authenticated - clearing menus")
        this.menuList.length = 0
        this.clearCache()
      } else if (this.isUserSuperuser()) {
        // Superuser gets static menu
        console.log("👑 Superuser detected - loading static menus")
        this.menuList.length = 0
        this.menuList.push(...staticMenuList)
        // Don't cache static menus as they don't change
      } else {
        // Regular user gets dynamic menu from backend
        console.log("👤 Regular user detected - loading dynamic menus")
        const dynamicMenus = await this.loadDynamicMenus()
        this.menuList.length = 0
        this.menuList.push(...dynamicMenus)

        // Cache the dynamic menus
        if (dynamicMenus.length > 0) {
          this.cacheMenus(dynamicMenus)
        }
      }

      console.log("✅ Menu initialization complete:", {
        menuCount: this.menuList.length,
        userType: this.currentUserType,
      })

      // Notify all subscribers about the update
      this.notifySubscribers()
    } catch (error) {
      console.error("❌ Failed to initialize menus:", error)
      this.error = error instanceof Error ? error.message : "Failed to load menus"
    } finally {
      this.isLoading = false
    }
  }

  // Public getters
  public getMenuList(): MenuItem[] {
    return [...this.menuList]
  }

  public getIsLoading(): boolean {
    return this.isLoading
  }

  public getError(): string | null {
    return this.error
  }

  public getIsAuthenticated(): boolean {
    return this.isAuthenticated()
  }

  public getIsSuperuser(): boolean {
    return this.isUserSuperuser()
  }

  // Force refresh menus
  public async refreshMenus(): Promise<void> {
    await this.initializeMenus(true)
  }

  // Cleanup
  public destroy(): void {
    if (this.authCheckInterval) {
      clearInterval(this.authCheckInterval)
    }
    this.subscribers.clear()
  }
}

// Static menu for superusers (keeping your original static menu)
const staticMenuList: MenuItem[] = [
  {
    title: "General",
    lanClass: "lan-1",
    Items: [
      {
        title: "Dashboards",
        id: 1,
        icon: "home",
        type: "sub",
        lanClass: "lan-3",
        children: [
          { path: `${process.env.PUBLIC_URL}/dashboard`, title: "Dashboard", type: "link", lanClass: "lan-4" },
          { path: `${process.env.PUBLIC_URL}/analytics`, title: "Analytics", type: "link", lanClass: "lan-4"},
          
        ],
      },
      {
        title: "User Management",
        id: 2,
        icon: "user",
        type: "sub",
        lanClass: "lan-6",
        active: false,
        children: [{ path: `${process.env.PUBLIC_URL}/users`, title: "Users", type: "link" }],
      },
      {
        title: "Core Settings",
        id: 3,
        icon: "widget",
        type: "sub",
        lanClass: "lan-6",
        active: false,
        children: [
          { path: `${process.env.PUBLIC_URL}/identification`, title: "Identification", type: "link" },
          { path: `${process.env.PUBLIC_URL}/standcategory`, title: "Stand Category", type: "link" },
          { path: `${process.env.PUBLIC_URL}/exhibitiontype`, title: "Exhibition Type", type: "link" },
          { path: `${process.env.PUBLIC_URL}/exhibitor-Type`, title: "Exhibitor Type", type: "link" },
          { path: `${process.env.PUBLIC_URL}/roles`, title: "Roles", type: "link" },
          { path: `${process.env.PUBLIC_URL}/menus`, title: "Menus", type: "link" },
          { path: `${process.env.PUBLIC_URL}/turnstiles`, title: "Turnstiles", type: "link" },
          { path: `${process.env.PUBLIC_URL}/turnstile-assignments`, title: "Turnstile Assignment", type: "link" },
          { path: `${process.env.PUBLIC_URL}/gate-assignments`, title: "Gate Assignment", type: "link" },
        ],
      },
    ],
  },
  {
    title: "Applications",
    lanClass: "lan-8",
    Items: [
      {
        title: "Exhibitions",
        id: 4,
        icon: "project",
        type: "sub",
        active: false,
        children: [
          { path: `${process.env.PUBLIC_URL}/exhibition`, type: "link", title: "exhibition" },
          { path: `${process.env.PUBLIC_URL}/exhibition-statistics`, type: "link", title: "Statistics" },
        ],
      },
      {
        title: "Exhibitors",
        icon: "layout",
        type: "sub",
        active: false,
        children: [
          { path: `${process.env.PUBLIC_URL}/exhibitors`, type: "link", title: "Exhibitors" },
          { path: `${process.env.PUBLIC_URL}/access-management`, type: "link", title: "Accessibility" },
        ],
      },
          { path: `${process.env.PUBLIC_URL}/exhibitors-management`, icon:"learning", type: "link", title: "Manage Companies" },
       
      {
        title: "Printing Badges",
        icon: "sample-page",
        type: "sub",
        active: false,
        children: [{ path: `${process.env.PUBLIC_URL}/printing-badges`, type: "link", title: "Printing Badges" }],
      },
      
      { path: `${process.env.PUBLIC_URL}/orders-verification`, icon: "task", title: "Verification", type: "link" },
      { path: `${process.env.PUBLIC_URL}/my-badge-orders`, icon: "task", title: "My Orders", type: "link" },
      { path: `${process.env.PUBLIC_URL}/staff-cars`, icon: "widget", title: "Staff Cars", type: "link" },
      {
        title: "Badge Entries",
        icon: "sample-page",
        type: "sub",
        active: false,
        children: [{ path: `${process.env.PUBLIC_URL}/badge-entries`, type: "link", title: "Badge Entries" }],
      },
      { path: `${process.env.PUBLIC_URL}/ticketing`, icon: "file", title: "Ticketing", type: "link" },
      { path: `${process.env.PUBLIC_URL}/ticket-purchase`, icon: "file", title: "Purchase Ticket", type: "link" },
      { path: `${process.env.PUBLIC_URL}/vehicle-visits`, icon: "task", title: "Vehicle Entries", type: "link" },
      { path: `${process.env.PUBLIC_URL}/performance`, icon: "widget", title: "Performance", type: "link" },
    ],
  },

  {
    title: "Reporting",
    lanClass: "lan-10",
    Items: [
      {
        title: "Badge Reporting",
        id: 5,
        icon: "file",
        type: "sub",
        active: false,
        children: [
          {path: `${process.env.PUBLIC_URL}/badge-reporting`, title: "Badge Report", type: "link" },

        ],
          
        },
      {
        title: "Entry Reporting", 
        id: 6,
        icon: "file",
        type: "sub",
        active: false,
        children: [
          {path: `${process.env.PUBLIC_URL}/entry-reporting`, title: "Entry Report", type: "link" },
        ],
      },
    ]
  },

]


// Real-time authentication and menu monitoring
const startRealtimeMonitoring = () => {
  const lastFetchTime = 0
  let currentUserType: "superuser" | "regular" | "unauthenticated" = "unauthenticated"
  let authCheckInterval: NodeJS.Timeout | null = null

  // Monitor localStorage changes (cross-tab synchronization)
  window.addEventListener("storage", (e) => {
    if (e.key === "accessToken") {
      menuStateManager.initializeMenus(true)
    }
  })

  // Monitor window focus to refresh stale data
  window.addEventListener("focus", () => {
    const now = Date.now()
    if (now - lastFetchTime > AUTH_CHECK_INTERVAL) {
      menuStateManager.initializeMenus()
    }
  })

  // Periodic authentication check
  authCheckInterval = setInterval(() => {
    menuStateManager.initializeMenus()
  }, 30000)

  // Check for auth changes every 5 seconds
  setInterval(() => {
    const newUserType = !menuStateManager.getIsAuthenticated()
      ? "unauthenticated"
      : menuStateManager.getIsSuperuser()
        ? "superuser"
        : "regular"

    if (newUserType !== currentUserType) {
      currentUserType = newUserType
      menuStateManager.initializeMenus(true)
    }
  }, AUTH_CHECK_INTERVAL)
}

// Create singleton instance
const menuStateManager = MenuStateManager.getInstance()

// Start monitoring immediately
startRealtimeMonitoring()

// Export the current menu list (reactive)
export const MenuList: MenuItem[] = new Proxy([], {
  get(target, prop) {
    const currentMenus = menuStateManager.getMenuList()
    if (prop === "length") return currentMenus.length
    if (typeof prop === "string" && !isNaN(Number(prop))) {
      return currentMenus[Number(prop)]
    }
    if (typeof prop === "symbol" && prop === Symbol.iterator) {
      return currentMenus[Symbol.iterator].bind(currentMenus)
    }
    return (currentMenus as any)[prop]
  },
})

// Export reactive state getters
export const getMenuState = () => ({
  menuList: menuStateManager.getMenuList(),
  isLoading: menuStateManager.getIsLoading(),
  error: menuStateManager.getError(),
  isAuthenticated: menuStateManager.getIsAuthenticated(),
  isSuperuser: menuStateManager.getIsSuperuser(),
})

// Export subscription function for React components
export const subscribeToMenuChanges = (callback: () => void): (() => void) => {
  return menuStateManager.subscribe(callback)
}

// Export refresh function
export const refreshMenuList = async (): Promise<void> => {
  await menuStateManager.refreshMenus()
}

// Additional utility functions for better integration
export const forceMenuRefresh = () => {
  menuStateManager.refreshMenus()
}

export const getMenuLoadingState = () => menuStateManager.getIsLoading()

export const getCurrentUserType = () => {
  if (!menuStateManager.getIsAuthenticated()) return "unauthenticated"
  return menuStateManager.getIsSuperuser() ? "superuser" : "regular"
}

// Export function to trigger menu refresh on login
export const handleUserLogin = async (): Promise<void> => {
  await menuStateManager.refreshMenus()
}

// Function to call after logout
export const handleUserLogout = (): void => {
  menuStateManager.initializeMenus(true)
}

// Initialize on module load
menuStateManager.initializeMenus()

// Event system for real-time updates
const menuUpdateCallbacks = new Set<() => void>()

// Subscribe to menu updates
export const subscribeToMenuUpdates = (callback: () => void): (() => void) => {
  menuUpdateCallbacks.add(callback)
  return () => menuUpdateCallbacks.delete(callback)
}

// Notify all subscribers
const notifyMenuUpdate = () => {
  menuUpdateCallbacks.forEach((callback) => {
    try {
      callback()
    } catch (error) {
      console.error("Error in menu update callback:", error)
    }
  })
}
